import { FC, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Minus, Plus, Search } from 'lucide-react';

import { StepDirection } from '@/components/blocks/berth-plan/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Marks controls that move the selection (find, conflict stepper). Clicking
 * them must not count as a click outside the open editor, which would clear
 * the selection before the step and restart it from the first visit.
 */
export const STEPS_SELECTION_ATTRIBUTE = 'data-steps-selection';

const stepsSelection = { [STEPS_SELECTION_ATTRIBUTE]: '' };

interface BerthPlanToolbarProps {
  query: string;
  onQueryChange: (query: string) => void;
  /** Matching visits, or `null` when there is no search. */
  matchCount: number | null;
  onFindNext: () => void;
  conflictCount: number;
  /** 1-based position of the selected visit among the conflicts, else 0. */
  conflictPosition: number;
  onConflictStep: (direction: StepDirection) => void;
  /** Badge for visits that aren't on the plan, next to the conflicts. */
  unplaced: ReactNode;
  /** History exists only before a cut-off. */
  canShowHistory: boolean;
  showHistory: boolean;
  onShowHistoryChange: (show: boolean) => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

const BerthPlanToolbar: FC<BerthPlanToolbarProps> = ({
  query,
  onQueryChange,
  matchCount,
  onFindNext,
  conflictCount,
  conflictPosition,
  onConflictStep,
  unplaced,
  canShowHistory,
  showHistory,
  onShowHistoryChange,
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
}) => {
  const { t } = useTranslation();

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onFindNext();
  };

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <div
        {...stepsSelection}
        className='relative w-64 max-w-full'>
        <Search className='pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          type='search'
          value={query}
          onChange={(event) => {
            onQueryChange(event.target.value);
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder={t('berthPlan.find.placeholder')}
          aria-label={t('berthPlan.find.placeholder')}
          className='h-9 pl-8'
        />
      </div>
      {matchCount !== null && (
        <span
          className='text-xs text-muted-foreground'
          aria-live='polite'>
          {t('berthPlan.find.matches', { count: matchCount })}
        </span>
      )}
      {conflictCount > 0 && (
        <div
          {...stepsSelection}
          className='inline-flex items-center rounded-md border border-destructive/30 bg-destructive/10 text-xs font-medium text-destructive'>
          <Button
            variant='ghost'
            size='icon'
            className='size-8 text-destructive hover:bg-destructive/10 hover:text-destructive'
            aria-label={t('berthPlan.conflicts.previous')}
            onClick={() => {
              onConflictStep(-1);
            }}>
            <ChevronLeft />
          </Button>
          <span className='px-1'>
            {conflictPosition > 0
              ? t('berthPlan.conflicts.position', {
                  position: conflictPosition,
                  count: conflictCount,
                })
              : t('berthPlan.conflicts.count', {
                  count: conflictCount,
                })}
          </span>
          <Button
            variant='ghost'
            size='icon'
            className='size-8 text-destructive hover:bg-destructive/10 hover:text-destructive'
            aria-label={t('berthPlan.conflicts.next')}
            onClick={() => {
              onConflictStep(1);
            }}>
            <ChevronRight />
          </Button>
        </div>
      )}
      {unplaced}
      <div className='ml-auto flex items-center gap-2'>
        {canShowHistory && (
          <Button
            variant='outline'
            size='sm'
            aria-pressed={showHistory}
            onClick={() => {
              onShowHistoryChange(!showHistory);
            }}>
            {showHistory
              ? t('berthPlan.history.hide')
              : t('berthPlan.history.show')}
          </Button>
        )}
        <div
          role='group'
          aria-label={t('berthPlan.zoom.label')}
          className='inline-flex rounded-md border'>
          <Button
            variant='ghost'
            size='icon'
            className='size-9 rounded-r-none'
            disabled={!canZoomOut}
            aria-label={t('berthPlan.zoom.out')}
            onClick={onZoomOut}>
            <Minus />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='size-9 rounded-l-none border-l'
            disabled={!canZoomIn}
            aria-label={t('berthPlan.zoom.in')}
            onClick={onZoomIn}>
            <Plus />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BerthPlanToolbar;
